// US-56 — full-screen overlay «Сервис временно недоступен».
//
// Появляется поверх всего UI когда api/client.ts детектит 5xx/network ошибку,
// или когда ErrorBoundary поймал unhandled JS exception. Параллельно пингует
// /api/health/ каждые 15 сек и автоматически закрывается при первом 200 OK
// (если reason !== "crash" — крэш JS требует ручного reload).

import { useEffect, useState } from "react";
import { Stack, Text, Title, Button, Loader, Group } from "@mantine/core";
import { AlertTriangle, RefreshCcw, Wrench } from "lucide-react";
import {
  exitMaintenance,
  getMaintenanceState,
  subscribe,
  type MaintenanceState,
} from "./state";

const HEALTH_POLL_INTERVAL_MS = 15_000;

interface OverlayContentProps {
  state: MaintenanceState;
  onRetry: () => void;
  isProbing: boolean;
}

function OverlayContent({ state, onRetry, isProbing }: OverlayContentProps) {
  const isCrash = state.reason === "crash";
  const headline = isCrash
    ? "Что-то пошло не так"
    : "Сервис временно недоступен";
  const sub = isCrash
    ? "Произошла непредвиденная ошибка. Обновите страницу — если проблема повторится, сообщите администратору."
    : "Ведутся технические работы. Мы сами пробуем восстановить соединение каждые 15 секунд. Можно подождать или попробовать обновить вручную.";

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10_000,
        background: "rgba(15, 14, 12, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: "100%",
          background: "#ffffff",
          color: "#1f1d18",
          borderRadius: 16,
          padding: "32px 36px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.32)",
          fontFamily: "Inter, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
        }}
      >
        <Stack gap={18}>
          <Group gap={12} align="center">
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: isCrash ? "#fde6dc" : "#f1e1bd",
                color: isCrash ? "#c14a2a" : "#bf8a3a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isCrash ? <AlertTriangle size={22} /> : <Wrench size={22} />}
            </div>
            <div>
              <Text
                fz="xs"
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: "0.15em", color: "#8a8472" }}
              >
                SKEDD
              </Text>
              <Title order={3} style={{ marginTop: 2, fontSize: 22, fontWeight: 600 }}>
                {headline}
              </Title>
            </div>
          </Group>
          <Text size="sm" style={{ lineHeight: 1.5, color: "#3f3a30" }}>
            {sub}
          </Text>
          {!isCrash && isProbing ? (
            <Group gap={8} align="center">
              <Loader size="xs" color="gray" />
              <Text size="xs" c="dimmed">
                Проверяем доступность…
              </Text>
            </Group>
          ) : null}
          <Group gap={8} mt={8}>
            <Button
              onClick={onRetry}
              leftSection={<RefreshCcw size={14} />}
              color="brand"
            >
              {isCrash ? "Обновить страницу" : "Попробовать сейчас"}
            </Button>
            {!isCrash ? (
              <Button
                onClick={() => {
                  window.location.reload();
                }}
                variant="subtle"
                color="gray"
              >
                Перезагрузить
              </Button>
            ) : null}
          </Group>
        </Stack>
      </div>
    </div>
  );
}

export function MaintenanceOverlay() {
  const [state, setState] = useState<MaintenanceState>(() => getMaintenanceState());
  const [isProbing, setIsProbing] = useState(false);

  // Подписка на module-level state.
  useEffect(() => {
    return subscribe((next) => setState(next));
  }, []);

  // Auto-probe /api/health/ каждые 15 сек пока в maintenance и не crash.
  useEffect(() => {
    if (!state.active || state.reason === "crash") return;
    let stopped = false;

    const probe = async () => {
      if (stopped) return;
      setIsProbing(true);
      try {
        const resp = await fetch("/api/health/", {
          credentials: "same-origin",
          // Health endpoint — лёгкий, читаемый каждые 15 сек, без кеширования.
          cache: "no-store",
        });
        if (!stopped && resp.ok) {
          exitMaintenance();
        }
      } catch {
        // Сеть ещё лежит — продолжаем поллить.
      } finally {
        if (!stopped) setIsProbing(false);
      }
    };

    // Первый пинг через 3 секунды (чтобы overlay успел отрисоваться),
    // дальше — каждые HEALTH_POLL_INTERVAL_MS.
    const initial = window.setTimeout(probe, 3_000);
    const interval = window.setInterval(probe, HEALTH_POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  }, [state.active, state.reason]);

  if (!state.active) return null;

  const handleRetry = () => {
    if (state.reason === "crash") {
      window.location.reload();
      return;
    }
    // Принудительный one-shot пробник: пробуем выйти из maintenance немедленно.
    fetch("/api/health/", { credentials: "same-origin", cache: "no-store" })
      .then((resp) => {
        if (resp.ok) exitMaintenance();
      })
      .catch(() => {
        /* остаёмся в maintenance */
      });
  };

  return (
    <OverlayContent state={state} onRetry={handleRetry} isProbing={isProbing} />
  );
}
